import { ChevronDown } from "lucide-react";
import { ReactNode } from "react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";

export interface CarePlanItemPickerProps<T> {
  icon: ReactNode;
  label: string;
  addButtonText: string;
  searchPlaceholder: string;
  emptyText: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  items: T[];
  getItemId: (item: T) => string;
  onSelectItem: (id: string) => void;
  renderItem: (item: T) => ReactNode;
}

export const CarePlanItemPicker = <T,>({
  icon,
  label,
  addButtonText,
  searchPlaceholder,
  emptyText,
  searchValue,
  onSearchChange,
  isLoading,
  popoverOpen,
  onPopoverOpenChange,
  items,
  getItemId,
  onSelectItem,
  renderItem,
}: CarePlanItemPickerProps<T>) => {
  return (
    <span className="flex justify-between items-center">
      <Label>
        {icon}
        {label}
      </Label>
      <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="font-light min-w-[130px] justify-between text-muted-foreground"
          >
            {addButtonText}
            <ChevronDown size={14} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end" onWheelCapture={(e) => e.stopPropagation()}>
          <Command shouldFilter={false}>
            <CommandInput placeholder={searchPlaceholder} value={searchValue} onValueChange={onSearchChange} />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="xs" />
                </div>
              ) : (
                <>
                  <CommandEmpty>{emptyText}</CommandEmpty>
                  <CommandGroup>
                    {items.map((item) => {
                      const id = getItemId(item);
                      return (
                        <CommandItem key={id} value={id} onSelect={() => onSelectItem(id)} className="cursor-pointer">
                          {renderItem(item)}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </span>
  );
};
